// =============================================================================
// NAVBAR — Main Navigation Component
// =============================================================================
// Top navigation bar engineered for clear differentiation across ALL screen sizes:
//
// 1. CLEAR VISUAL DIFFERENTIATION:
//    Always renders with a distinct background (sleek translucent glassmorphism
//    over hero, solid themed surface when scrolled) with crisp border and contrast.
//
// 2. ZERO TEXT WRAPPING:
//    Brand name and navigation items are locked to a single line (`white-space: nowrap`)
//    with overflow protection.
//
// 3. RESPONSIVE HAMBURGER (< 1024px):
//    If the screen is too narrow for all links to fit horizontally without crowding,
//    it cleanly shifts to the hamburger navigation drawer.
//
// 4. ACCESSIBLE MOBILE DRAWER:
//    The drawer includes all navigation links, the language switcher, and the primary CTA.
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useScrolled } from '../hooks/useScrolled.js';
import { useMediaQuery, BREAKPOINT_LG } from '../hooks/useMediaQuery.js';
import { CloseIcon } from './icons.js';
import './Navbar.css';

export interface NavLink {
  /** Display text (should be translated via i18n) */
  label: string;
  /** URL path (e.g., "#menu", "#about", "#contact") */
  href: string;
  /** Unique key for React rendering */
  id: string;
}

export interface NavbarProps {
  /** The restaurant's display name or logo */
  siteName: string;
  /** Optional logo image URL */
  logo?: string;
  /** Navigation links */
  links: NavLink[];
  /** Primary CTA button text (e.g., "Reserve a Table", "Order Online") */
  ctaText?: string;
  /** Primary CTA button URL */
  ctaHref?: string;
  /** Language switcher React element */
  languageSwitcher?: React.ReactNode;
}

export function Navbar({ siteName, logo, links, ctaText, ctaHref, languageSwitcher }: NavbarProps) {
  const isScrolled = useScrolled(40);
  const isDesktop = useMediaQuery(BREAKPOINT_LG); // ≥ 1024px
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    if (isDesktop) {
      setIsMenuOpen(false);
    }
  }, [isDesktop]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen((prev) => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  return (
    <header
      className={`navbar ${isScrolled ? 'is-scrolled' : 'is-top'} ${isMenuOpen ? 'menu-open' : ''}`}
      role="banner"
    >
      <nav className="navbar-inner container" aria-label="Main navigation">
        {/* --- Brand / Logo --- */}
        <a href="/" className="navbar-brand" aria-label={`${siteName} - Home`}>
          {logo ? (
            <img
              src={logo}
              alt={`${siteName} logo`}
              className="navbar-logo"
              width={140}
              height={40}
            />
          ) : (
            <span className="navbar-name">{siteName}</span>
          )}
        </a>

        {/* --- Desktop Nav Links (Visible only on ≥ 1024px) --- */}
        <ul className="navbar-links" role="list">
          {links.map((link) => (
            <li key={link.id}>
              <a href={link.href} className="navbar-link">
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* --- Right Actions (Language Switcher, CTA, Hamburger) --- */}
        <div className="navbar-actions">
          {/* Language Switcher */}
          {languageSwitcher && <div className="navbar-lang-slot">{languageSwitcher}</div>}

          {/* Primary CTA (Visible on desktop & tablet) */}
          {ctaText && ctaHref && (
            <a href={ctaHref} className="btn btn-primary navbar-cta">
              {ctaText}
            </a>
          )}

          {/* Hamburger Toggle (Visible on < 1024px) */}
          <button
            type="button"
            className="navbar-hamburger"
            onClick={toggleMenu}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation-drawer"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <span className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`} />
            <span className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`} />
            <span className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`} />
          </button>
        </div>
      </nav>

      {/* --- Fullscreen Mobile Navigation Drawer (mounted via Portal) --- */}
      {isMenuOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="navbar-mobile-drawer"
            id="mobile-navigation-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="mobile-drawer-header">
              <span className="mobile-drawer-brand">{siteName}</span>
              <button
                type="button"
                className="mobile-drawer-close"
                onClick={closeMenu}
                aria-label="Close navigation"
              >
                <CloseIcon width={24} height={24} />
              </button>
            </div>

            <div className="mobile-drawer-content">
              <ul className="mobile-drawer-links" role="list">
                {links.map((link) => (
                  <li key={link.id}>
                    <a href={link.href} className="mobile-drawer-link" onClick={closeMenu}>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="mobile-drawer-footer">
                {ctaText && ctaHref && (
                  <a
                    href={ctaHref}
                    className="btn btn-primary btn-lg mobile-drawer-cta"
                    onClick={closeMenu}
                  >
                    {ctaText}
                  </a>
                )}

                {languageSwitcher && <div className="mobile-drawer-lang">{languageSwitcher}</div>}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </header>
  );
}
